import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import { getInitData, getTelegramPlatform, isInsideTelegram } from '@/telegramWebApp'
import { i18n } from '@/i18n'

export type LoginConfig =
  | { state: 'ready'; clientId: number; nonce: string }
  | { state: 'off' }
  | { state: 'failed' }

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'error'

interface AuthState {
  token: string | null
  status: AuthStatus
  errorMessage: string | null
  telegramUserId: number | null
  username: string | null
  isAdmin: boolean
}

// Reads a failed fetch Response's JSON `detail` field, falling back to statusText -- the backend
// (FastAPI/HTTPException) always sends `{"detail": "..."}` for auth failures, and we want that
// real reason surfaced (and logged to Sentry) instead of a generic message.
async function readErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: string }
    if (body.detail) return body.detail
  } catch {
    // response wasn't JSON -- keep statusText
  }
  return response.statusText || `HTTP ${response.status}`
}

// The session a plain browser keeps (the answer of a login, as the server sent it), so an F5 or a later visit does not ask
// to log in again. It is renewed on every restore (`POST /api/auth/refresh`), so it lasts 30 days from the last visit.
// Storage can be missing or blocked: everything here then just does not stick.
const SESSION_KEY = 'auth.session'

interface StoredSession {
  access_token: string
  telegram_user_id: number
  username: string | null
  is_admin: boolean
}

function readStoredSession(): StoredSession | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null') as Partial<StoredSession> | null
    if (
      parsed &&
      typeof parsed.access_token === 'string' &&
      typeof parsed.telegram_user_id === 'number' &&
      typeof parsed.is_admin === 'boolean' &&
      (parsed.username === null || typeof parsed.username === 'string')
    ) {
      return parsed as StoredSession
    }
  } catch {
    // nothing kept, damaged or storage unavailable
  }
  return null
}

function writeStoredSession(session: StoredSession | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    else localStorage.removeItem(SESSION_KEY)
  } catch {
    // storage unavailable -- the login just won't stick
  }
}

const TRANSIENT_STATUSES = new Set([502, 503, 504])
const RETRY_DELAYS_MS = [500, 1500]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// A 502/503/504 on this endpoint has been observed as a transient gateway blip (Bugsink
// SYNC_TODO_FRONTEND-5/-6) rather than a real auth rejection -- retry a couple of times with a
// short backoff before treating it as a hard failure.
async function fetchWithTransientRetry(initData: string): Promise<Response> {
  let response = await fetch('/api/auth/telegram-webapp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ init_data: initData }),
  })
  for (const ms of RETRY_DELAYS_MS) {
    if (!TRANSIENT_STATUSES.has(response.status)) break
    await delay(ms)
    response = await fetch('/api/auth/telegram-webapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: initData }),
    })
  }
  return response
}

// Dedup lock for concurrent signIn() callers (e.g. the router guard firing for two quick
// navigations) -- kept outside the store's reactive state since a Promise has no business being
// a reactive value; everyone just awaits the same in-flight attempt rather than double-POSTing.
let inFlight: Promise<void> | null = null

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    status: 'idle',
    errorMessage: null,
    telegramUserId: null,
    username: null,
    isAdmin: false,
  }),
  actions: {
    async signIn(): Promise<void> {
      if (this.status === 'loading') return
      if (inFlight) {
        await inFlight
        return
      }
      inFlight = this.performSignIn()
      try {
        await inFlight
      } finally {
        inFlight = null
      }
    },

    async performSignIn(): Promise<void> {
      this.status = 'loading'
      this.errorMessage = null

      const initData = getInitData()
      if (!initData) {
        // Not opened via Telegram (e.g. local dev in a plain browser, or the page shared as a
        // plain link). No point asking the backend for a Telegram login -- there's no initData to send --
        // but a session kept from an earlier visit may still be good.
        if (await this.restore()) return
        this.status = 'error'
        this.errorMessage = i18n.global.t(isInsideTelegram() ? 'auth.sessionExpired' : 'auth.notTelegram')
        return
      }

      let response: Response
      try {
        response = await fetchWithTransientRetry(initData)
      } catch (e) {
        this.status = 'error'
        this.errorMessage = i18n.global.t('auth.unreachable')
        Sentry.captureException(e, {
          extra: { stage: 'auth-network', isInsideTelegram: isInsideTelegram(), platform: getTelegramPlatform() },
        })
        return
      }

      if (!response.ok) {
        const detail = await readErrorDetail(response)
        this.status = 'error'
        // (inside Telegram, a 401 is an initData that got too old, the server's English reason is no help)
        this.errorMessage =
          response.status === 401 && isInsideTelegram() ? i18n.global.t('auth.sessionExpired') : detail
        Sentry.captureException(new Error(detail), {
          extra: {
            stage: 'auth-rejected',
            status: response.status,
            isInsideTelegram: isInsideTelegram(),
            platform: getTelegramPlatform(),
          },
        })
        return
      }

      await this.applySession(response)
    },

    // Fills the store from a successful `AuthTokenResponse`, whichever way the user logged in.
    async applySession(response: Response): Promise<void> {
      const data = (await response.json()) as StoredSession
      this.token = data.access_token
      this.telegramUserId = data.telegram_user_id
      this.username = data.username
      this.isAdmin = data.is_admin
      this.status = 'authenticated'
      writeStoredSession(data)
    },

    // Logs in again with the session kept from an earlier visit (the server swaps it for a fresh one). A session the
    // server rejects is forgotten; when it cannot be asked (offline, a gateway blip) the session stays for the next try.
    async restore(): Promise<boolean> {
      const kept = readStoredSession()
      if (!kept) return false
      let response: Response
      try {
        response = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { Authorization: `Bearer ${kept.access_token}` },
        })
      } catch {
        return false
      }
      if (response.status >= 500) return false
      if (!response.ok) {
        writeStoredSession(null)
        return false
      }
      await this.applySession(response)
      return true
    },

    // The bot's Client ID and a fresh nonce for "Log in with Telegram" in a plain browser. `off`: the backend answered
    // and has no Client ID (nothing to offer); `failed`: it could not be asked (reported, so a broken endpoint shows up).
    async fetchLoginConfig(): Promise<LoginConfig> {
      try {
        const response = await fetch('/api/auth/config')
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = (await response.json()) as {
          telegram_login_client_id: number | null
          nonce: string | null
        }
        return data.telegram_login_client_id !== null && data.nonce
          ? { state: 'ready', clientId: data.telegram_login_client_id, nonce: data.nonce }
          : { state: 'off' }
      } catch (e) {
        Sentry.captureException(e, { extra: { stage: 'login-config' } })
        return { state: 'failed' }
      }
    },

    // The browser login: trades the id_token of the "Log in with Telegram" popup for a session.
    async signInWithTelegramLogin(idToken: string, nonce: string): Promise<void> {
      this.status = 'loading'
      this.errorMessage = null
      let response: Response
      try {
        response = await fetch('/api/auth/telegram-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: idToken, nonce }),
        })
      } catch (e) {
        this.status = 'error'
        this.errorMessage = i18n.global.t('auth.unreachable')
        Sentry.captureException(e, { extra: { stage: 'telegram-login-network' } })
        return
      }
      if (!response.ok) {
        const detail = await readErrorDetail(response)
        this.status = 'error'
        this.errorMessage = detail
        Sentry.captureException(new Error(detail), {
          extra: { stage: 'telegram-login-rejected', status: response.status },
        })
        return
      }
      await this.applySession(response)
    },

    // Called when a request comes back 401 mid-session (expired/invalid JWT). Drops back to
    // 'idle' so the router guard re-attempts sign-in on the next navigation.
    reset(): void {
      writeStoredSession(null)
      this.token = null
      this.status = 'idle'
      this.errorMessage = null
      this.telegramUserId = null
      this.username = null
      this.isAdmin = false
    },
  },
})
